/**
 * PLANUNG (Soll-Zustand) = Bestand + Änderungen.
 *
 * Hier stehen nur die Abweichungen vom Bestand. Wände werden über einen Punkt gefunden,
 * der in der Wand liegt (Planmaße in Metern, wie im Grundriss: x von der Garagenseite, y von der Straße).
 * Koordinaten findest du in der App mit „Messen“ oder im Auswahlfeld eines Möbels (X/Y in cm).
 *
 * Beispiele:
 *   removeWall(1, 3.18, 1.0);                       // EG: Wand bei x=3,18 / y=1,00 entfernen
 *   addOpening(1, 1.0, 3.96, door(0.6, 1.6));       // EG: Durchbruch 1,00 m breit in die Wand bei (1,0 | 3,96)
 *   addOpening(0, 2.5, 5.13, hole(1.0, 2.0, 0, 2.2)); // UG: Durchbruch ohne Zarge
 *   removeOpening(2, 3.1, 6.58);                     // OG: Öffnung (Tür/Fenster) an dieser Stelle zumachen
 *   addWall(W(1, 0.3, 7.5, 3.0, 7.62));               // EG: neue Wand (12 cm) einziehen
 *   renameRoom("r8", "Wohnen & Essen");               // Vorgabe-Raumname in der Planung
 *   moveRoomLabel("r9", 2.2, 4.4, "ca. 40 m²");        // Beschriftung verschieben / Fläche anpassen
 *   removeRoomLabel("r9");                            // Beschriftung weg (z. B. Raum zusammengelegt)
 */
import type { WallDef } from "./walls";
import { WALLS, door, win, fix, hole, W } from "./walls";
import { ROOMS, type RoomDef } from "./rooms";
import type { Opening } from "./geometry";

const walls: WallDef[] = WALLS.map((w) => ({ ...w, ops: (w.ops || []).map((o) => ({ ...o })) }));
const rooms: RoomDef[] = ROOMS.map((r) => ({ ...r }));
const problems: string[] = [];

function wallAt(f: number, x: number, y: number): WallDef | undefined {
  const tol = 0.02;
  return walls.find((w) => w.f === f && x >= w.x0 - tol && x <= w.x1 + tol && y >= w.y0 - tol && y <= w.y1 + tol);
}
function need(f: number, x: number, y: number, what: string) {
  const w = wallAt(f, x, y);
  if (!w) problems.push(`${what}: keine Wand bei Geschoss ${f}, x=${x}, y=${y}`);
  return w;
}

export function removeWall(f: number, x: number, y: number) {
  const w = need(f, x, y, "removeWall"); if (w) walls.splice(walls.indexOf(w), 1);
}
export function addOpening(f: number, x: number, y: number, op: Opening) {
  const w = need(f, x, y, "addOpening"); if (w) w.ops = [...(w.ops || []), op];
}
/** entfernt die Öffnung, die an der Stelle (x, y) liegt */
export function removeOpening(f: number, x: number, y: number) {
  const w = need(f, x, y, "removeOpening"); if (!w) return;
  const along = w.x1 - w.x0 >= w.y1 - w.y0 ? x : y;
  const before = (w.ops || []).length;
  w.ops = (w.ops || []).filter((o) => !(along >= o.a0 - 0.02 && along <= o.a1 + 0.02));
  if (w.ops.length === before) problems.push(`removeOpening: keine Öffnung bei Geschoss ${f}, x=${x}, y=${y}`);
}
export function addWall(w: WallDef) { walls.push(w); }
export function renameRoom(key: string, name: string) { const r = rooms.find((r) => r.key === key); if (r) r.def = name; }
export function moveRoomLabel(key: string, x: number, y: number, area?: string) {
  const r = rooms.find((r) => r.key === key); if (r) { r.x = x; r.y = y; if (area) r.area = area; }
}
export function removeRoomLabel(key: string) { const i = rooms.findIndex((r) => r.key === key); if (i >= 0) rooms.splice(i, 1); }

/* ====================== ÄNDERUNGEN DER PLANUNG ====================== */

/* --- EG: Wohnraum mit den beiden Zimmern zur Garagenseite zusammenlegen --- */
removeWall(1, 1.0, 6.64);   // Wand zwischen Wohnraum (30,10) und Zimmer 10,01
addWall(W(1, 0.3, 6.52, 0.55, 6.76)); // davon bleibt an der Außenwand ein 25-cm-Backen stehen
removeWall(1, 1.0, 3.96);   // Wand zwischen Zimmer 10,01 und Zimmer 14,61
removeRoomLabel("r8");      // 10,01 m²
removeRoomLabel("r9");      // 14,61 m²
moveRoomLabel("r7", 3.8, 8.6, "ca. 55 m²");

/* --- EG: die beiden kleinen Räume an der Straße (2,81 + 1,89) zu einem Raum, Tür gerade vom Flur --- */
removeWall(1, 6.2, 1.0);    // Trennwand mit Tür
removeWall(1, 4.8, 1.97);   // Wandstück links vom bisherigen Durchgang
removeWall(1, 6.8, 1.97);   // Wand unter der Treppe
addWall(W(1, 4.48, 1.88, 7.45, 2.07, [door(4.88, 5.73)])); // durchgehende Wand, Tür mittig zum Flur (Flur x 4,48–6,13)
removeRoomLabel("r12");     // 1,89 m²
moveRoomLabel("r11", 5.96, 1.1, "ca. 4,9 m²");

/* --- UG: Flur zum Treppenraum und Vorraum vor dem Raum 17,08 mit je einer Wand + Tür abtrennen --- */
addWall(W(0, 4.26, 4.02, 4.5, 5.07, [door(4.14, 4.96)]));  // zwischen Flur und Treppenraum
addWall(W(0, 4.5, 5.52, 5.82, 5.64, [door(4.76, 5.58)]));  // zwischen Treppenraum und Vorraum zu 17,08


/* =================================================================== */

if (problems.length) console.warn("Planung: nicht angewendet\n" + problems.join("\n"));
export const PLANUNG_WALLS = walls;
export const PLANUNG_ROOMS = rooms;
// fürs Schreiben von Änderungen bequem verfügbar
export { door, win, fix, hole, W };
